// Hệ thống theme DiOwner: 5 màu chủ đạo (primary) × chế độ sáng/tối.
// Nguyên tắc cốt lõi: VỎ ứng dụng (--brand) tuỳ biến theo màu chủ đạo người dùng chọn;
// NỘI DUNG (tím/gold/teal/rose) và BẢNG MÀU BIỂU ĐỒ là CỐ ĐỊNH (colorblind-safe),
// KHÔNG phụ thuộc màu chủ đạo. "UI primary ≠ chart palette".
//
// Chỉ các biến VỎ được set động từ JS (theo màu + mode): --brand, --brand-deep,
// --brand-ink, --brand-header, --brand-header-ink, --adm-color-primary.
// Còn lại (content tokens + ground/card/ink...) định nghĩa cố định trong theme.scss.

// localStorage key riêng của DiOwner (hợp đồng hệ sinh thái: mỗi app một key riêng).
export const THEME_STORAGE_KEY = 'diowner_theme';

export const THEME_MODES = ['light', 'dark'];

// Trục MẬT ĐỘ (hợp đồng DiCRM AI): 'default' (Thoáng) | 'compact' (Gọn).
export const DENSITIES = ['default', 'compact'];

/**
 * 5 màu chủ đạo — tái sử dụng data từ hệ sinh thái DiCloud (hue tách bạch).
 * Mỗi màu có sắc cho mode sáng (`light`) và mode tối (`dark`, sáng hơn để nổi trên
 * nền đen), một sắc đậm (`deep`) cho hover/gradient, và `headerDark` — nền header
 * trầm & sang cho mode tối (mode sáng header dùng luôn màu `light`).
 */
export const PRIMARY_COLORS = [
  {
    key: 'pms',
    light: '#1677ff',
    dark: '#4096ff',
    deep: '#0e5fd8',
    headerDark: '#17204f',
  },
  {
    key: 'dipos',
    light: '#0f8480',
    dark: '#2fbfb8',
    deep: '#0c6c68',
    headerDark: '#0b3b38',
  },
  {
    key: 'diacc',
    light: '#fa541c',
    dark: '#f26d6a',
    deep: '#d8410f',
    headerDark: '#5a2814',
  },
  {
    key: 'dipo',
    light: '#2e7d32',
    dark: '#5fbf6a',
    deep: '#25642a',
    headerDark: '#153f18',
  },
  {
    key: 'dihrm',
    light: '#faad14',
    dark: '#ffc53d',
    deep: '#d99400',
    headerDark: '#4a3608',
  },
];

export const DEFAULT_PRIMARY_KEY = 'pms';

const COLOR_KEYS = PRIMARY_COLORS.map((c) => c.key);

// Hình dạng preference được hợp đồng hệ sinh thái chuẩn hoá:
//   { mode: 'light'|'dark', primaryColorKey: <1 trong 5 key>, density?: 'default'|'compact' }
export const DEFAULT_THEME = {
  mode: 'light',
  primaryColorKey: DEFAULT_PRIMARY_KEY,
  density: 'default',
};

/** Tìm option màu theo key (fallback về màu đầu tiên). */
const findColor = (key) =>
  PRIMARY_COLORS.find((c) => c.key === key) ?? PRIMARY_COLORS[0];

/** Hex màu chủ đạo (accent) theo key + mode. */
export function getPrimaryColor(key, mode) {
  const opt = findColor(key);
  return mode === 'dark' ? opt.dark : opt.light;
}

/** Hex nền header theo key + mode (sáng: dùng màu light; tối: sắc trầm headerDark). */
export function getHeaderColor(key, mode) {
  const opt = findColor(key);
  return mode === 'dark' ? opt.headerDark : opt.light;
}

/** Hex sắc đậm (hover/gradient) theo key + mode. */
export function getDeepColor(key, mode) {
  const opt = findColor(key);
  return mode === 'dark' ? opt.dark : opt.deep;
}

/**
 * Chọn màu chữ tương phản trên nền `hex` (đen/trắng) theo độ sáng tương đối.
 * Đảm bảo màu chủ đạo sáng (vd amber) vẫn đọc được chữ.
 */
export function contrastInk(hex) {
  const h = hex.replace('#', '');
  const r = parseInt(h.substring(0, 2), 16) / 255;
  const g = parseInt(h.substring(2, 4), 16) / 255;
  const b = parseInt(h.substring(4, 6), 16) / 255;
  const lin = (c) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
  const L = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
  return L > 0.55 ? '#0f172a' : '#ffffff';
}

/** Đọc preference từ localStorage, validate, fallback về mặc định. */
export function getInitialTheme() {
  try {
    const raw = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      // Backward-compat: bản cũ lưu `primaryKey`; hợp đồng đổi thành `primaryColorKey`.
      const storedKey = parsed.primaryColorKey ?? parsed.primaryKey;
      return {
        mode: THEME_MODES.includes(parsed.mode)
          ? parsed.mode
          : DEFAULT_THEME.mode,
        primaryColorKey: COLOR_KEYS.includes(storedKey)
          ? storedKey
          : DEFAULT_THEME.primaryColorKey,
        density: DENSITIES.includes(parsed.density)
          ? parsed.density
          : DEFAULT_THEME.density,
      };
    }
  } catch {
    // localStorage không khả dụng / JSON hỏng → dùng mặc định
  }
  return { ...DEFAULT_THEME };
}

/** Lưu preference vào localStorage key `diowner_theme`. */
export function saveTheme(pref) {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify(pref));
  } catch {
    // ignore — vẫn áp dụng runtime
  }
}

/**
 * Áp theme: stamp `data-theme` lên <html> (kích hoạt bộ token sáng/tối trong
 * theme.scss) và set 5 biến VỎ động lên :root. Content/chart tokens KHÔNG đụng tới.
 */
export function applyTheme(pref) {
  const root = document.documentElement;
  const mode = THEME_MODES.includes(pref.mode) ? pref.mode : DEFAULT_THEME.mode;
  // Backward-compat: chấp nhận `primaryKey` cũ nếu chưa có `primaryColorKey`.
  const storedKey = pref.primaryColorKey ?? pref.primaryKey;
  const key = COLOR_KEYS.includes(storedKey)
    ? storedKey
    : DEFAULT_THEME.primaryColorKey;
  const density = DENSITIES.includes(pref.density)
    ? pref.density
    : DEFAULT_THEME.density;

  const brand = getPrimaryColor(key, mode);
  const header = getHeaderColor(key, mode);

  root.setAttribute('data-theme', mode);
  // Trục mật độ: stamp data-density → CSS var padding/spacing (xem theme.scss).
  root.setAttribute('data-density', density);
  root.style.setProperty('--brand', brand);
  root.style.setProperty('--brand-deep', getDeepColor(key, mode));
  root.style.setProperty('--brand-ink', contrastInk(brand));
  root.style.setProperty('--brand-header', header);
  root.style.setProperty('--brand-header-ink', contrastInk(header));
  // antd-mobile đọc màu primary qua biến này (nút, active state...).
  root.style.setProperty('--adm-color-primary', brand);
}
